import { EDUCATION_STEPS } from '../../shifts/constants';

export function setOnboardingMigrationFlags(user, isExistingUser) {
  const clientFlags = user.settings.clientFlags;
  // Do not show onboarding 4.0 stuff for exisitng users
  if (isExistingUser && !clientFlags.onboarding4Relevant) {
    clientFlags.shiftCreatedViaShiftModal = true;
  } else if (!isExistingUser) {
    clientFlags.onboarding4Relevant = true;
    clientFlags[`onboarding4-${EDUCATION_STEPS.FIELDS_VISIBILITY_BUTTONS}-presented`] = true;
    clientFlags[`onboarding4-${EDUCATION_STEPS.SHIFT_THREE_DOT_OPTIONS}-presented`] = true;

    if (user.isOrgCreator) {
      clientFlags.initialPositionsAndUsersSetup = false;
      clientFlags.onboarding.finished = false;
    }
  }

  // If old shifts education was shown to this user, assume he has already seen this.
  if (user.settings.shiftsHelpShown) {
    clientFlags.shiftsIntroModalShown = true;
    clientFlags.shiftsTutorialAvailableShiftsShown = true;
    clientFlags.shiftsTutorialUnassignedShiftsShown = true;
    clientFlags.shiftsTutorialMultiselectShown = true;
    clientFlags.shiftsTutorialCopySchedulesShown = true;
  }

  if (!isExistingUser) {
    user.settings.clientFlags.premiumDiscovery._relevant = true;
  }
}

// eslint-disable-next-line complexity
export function setDefaultClientFlags(user) {
  if (!user.settings.clientFlags) {
    user.settings.clientFlags = {};
  }

  if (typeof user.settings.clientFlags.numberOfShiftsCreated !== 'number') {
    user.settings.clientFlags.numberOfShiftsCreated = 0;
  }

  if (typeof user.settings.clientFlags.shiftsEducationLastUnlock !== 'number') {
    user.settings.clientFlags.shiftsEducationLastUnlock = 0;
  }

  // this should be true by default
  if (!('showDurationsAsDecimals' in user.settings.clientFlags)) {
    user.settings.clientFlags.showDurationsAsDecimals = true;
  }

  if (!('whatsNewReadItems' in user.settings.clientFlags)) {
    user.settings.clientFlags.whatsNewReadItems = '';
  }

  if (!('timeoffReportColumns' in user.settings.clientFlags)) {
    user.settings.clientFlags.timeoffReportColumns = [
      { property: 'type', active: true },
      { property: 'requestedDays', active: true },
      { property: 'approvedDays', active: true },
    ];
  }

  if (!('shiftFeedbackReportColumns' in user.settings.clientFlags)) {
    user.settings.clientFlags.shiftFeedbackReportColumns = [
      { property: 'date', active: true },
      { property: 'employeeId', active: false },
      { property: 'location', active: true },
      { property: 'position', active: true },
      { property: 'groups', active: true },
      { property: 'tags', active: true },
      { property: 'dayPartsStringified', active: true },
      { property: 'clockInOutSummary', active: true },
      { property: 'rating', active: true },
      { property: 'comment', active: true },
    ];
  }

  addPotentiallyMissingColumn(user, 'shiftFeedbackReportColumns', 'fullLegalName', { active: false });

  removeObsoleteColumn(user, 'timeoffReportColumns', 'requested');
  removeObsoleteColumn(user, 'timeoffReportColumns', 'approved');
  addPotentiallyMissingColumn(user, 'timeoffReportColumns', 'approvedDays', { after: 'type' });
  addPotentiallyMissingColumn(user, 'timeoffReportColumns', 'approvedMinutes', {
    after: 'approvedDays',
  });

  /**
   * For now we're hiding the requested column due to https://github.com/getsling/sling-issues/issues/629
   */
  // addPotentiallyMissingColumn(user, 'timeoffReportColumns', 'requestedDays', { after: 'type' });
  removeObsoleteColumn(user, 'timeoffReportColumns', 'requestedDays');

  addPotentiallyMissingColumn(user, 'timeoffReportColumns', 'unpaidDays', {
    after: 'approvedDays',
  });
  addPotentiallyMissingColumn(user, 'timeoffReportColumns', 'unpaidMinutes', {
    after: 'unpaidDays',
  });

  addPotentiallyMissingColumn(user, 'timeoffReportColumns', 'deniedDays', {
    after: 'unpaidMinutes',
  });
  addPotentiallyMissingColumn(user, 'timeoffReportColumns', 'deniedMinutes', {
    after: 'deniedDays',
  });

  addPotentiallyMissingColumn(user, 'timeoffReportColumns', 'ptoCost', {
    after: 'deniedMinutes',
  });
  addPotentiallyMissingColumn(user, 'timeoffReportColumns', 'remaining', { after: 'type' });

  const defaultNoShowReportColumns = [
    { property: 'employeeId', active: true },
    { property: 'location', active: true },
    { property: 'position', active: true },
    { property: 'scheduled', active: true },
    { property: 'actual', active: true },
    { property: 'noShow', active: true },
    { property: 'sickCallout', active: true },
    { property: 'lateArrivals', active: true },
  ];

  if (!('noShowReportColumns' in user.settings.clientFlags)) {
    user.settings.clientFlags.noShowReportColumns = [...defaultNoShowReportColumns];
  }
  addPotentiallyMissingColumn(user, 'noShowReportColumns', 'scheduledMinutes', {
    after: 'scheduled',
  });
  addPotentiallyMissingColumn(user, 'noShowReportColumns', 'locationExternalId', {
    after: 'location',
  });
  addPotentiallyMissingColumn(user, 'noShowReportColumns', 'positionExternalId', {
    after: 'position',
  });
  addPotentiallyMissingColumn(user, 'noShowReportColumns', 'actualMinutes', { after: 'actual' });
  addPotentiallyMissingColumn(user, 'noShowReportColumns', 'sickCallout', { after: 'noShow' });
  addPotentiallyMissingColumn(user, 'noShowReportColumns', 'lateArrivals', {
    after: 'sickCallout',
  });

  if (!('timesheetsReportColumns' in user.settings.clientFlags)) {
    user.settings.clientFlags.timesheetsReportColumns = [
      { property: 'date', active: true },
      { property: 'employeeId', active: false },
      { property: 'position', active: true },
      { property: 'location', active: true },
      { property: 'tags', active: false },
      { property: 'clockedIn', active: true },
      { property: 'break', active: true },
      { property: 'clockedOut', active: true },
      { property: 'scheduled', active: true },
      { property: 'actual', active: true },
      { property: 'difference', active: true },
    ];
  }

  addPotentiallyMissingColumn(user, 'timesheetsReportColumns', 'tags', { after: 'position' });

  addPotentiallyMissingColumn(user, 'timesheetsReportColumns', 'dtstart', {
    after: 'tags',
    active: false,
  });

  addPotentiallyMissingColumn(user, 'timesheetsReportColumns', 'dtend', {
    after: 'dtstart',
    active: false,
  });

  addPotentiallyMissingColumn(user, 'timesheetsReportColumns', 'status', {
    after: 'difference',
    active: true,
  });

  removeObsoleteColumn(user, 'timesheetsReportColumns', 'employee');

  addPotentiallyMissingColumn(user, 'timesheetsReportColumns', 'clockOutDistanceFromLocation', {
    after: 'status',
    active: false,
  });
  addPotentiallyMissingColumn(user, 'timesheetsReportColumns', 'clockInDistanceFromLocation', {
    after: 'status',
    active: false,
  });
  addPotentiallyMissingColumn(user, 'timesheetsReportColumns', 'source', {
    after: 'difference',
    active: true,
  });
  addPotentiallyMissingColumn(user, 'timesheetsReportColumns', 'breakStart', {
    after: 'clockedIn',
    active: true,
  });
  addPotentiallyMissingColumn(user, 'timesheetsReportColumns', 'breakEnd', {
    after: 'breakStart',
    active: true,
  });
  addPotentiallyMissingColumn(user, 'timesheetsReportColumns', 'scheduledBreakDuration', {
    after: 'breakEnd',
    active: false,
  });
  addPotentiallyMissingColumn(user, 'timesheetsReportColumns', 'isAutoClockOut', {
    after: 'clockedOut',
    active: true,
  });
  addPotentiallyMissingColumn(user, 'timesheetsReportColumns', 'breakType', {
    after: 'clockedIn',
    active: true,
  });

  removeObsoleteColumn(user, 'timesheetsReportColumns', 'restBreak');
  removeObsoleteColumn(user, 'timesheetsReportColumns', 'restBreakEnd');
  removeObsoleteColumn(user, 'timesheetsReportColumns', 'restBreakStart');
  removeObsoleteColumn(user, 'timesheetsReportColumns', 'mealBreak');
  removeObsoleteColumn(user, 'timesheetsReportColumns', 'mealBreakEnd');
  removeObsoleteColumn(user, 'timesheetsReportColumns', 'mealBreakStart');
  removeObsoleteColumn(user, 'timesheetsReportColumns', 'scheduledRestBreak');
  removeObsoleteColumn(user, 'timesheetsReportColumns', 'scheduledMealBreak');

  //                                                old name with typo           valid name
  migrateColumn(user, 'timesheetsReportColumns', 'schduledBreakDuration', 'scheduledBreakDuration');

  if (!('onboarding' in user.settings.clientFlags)) {
    user.settings.clientFlags.onboarding = { skipSteps: {}, finishedSteps: [], finished: true };
  }

  const onboardingTrialFlags = [
    'onboardingTrial_addEmployees',
    'onboardingTrial_createShifts',
    'onboardingTrial_defineWages',
    'onboardingTrial_viewReports',
    'onboardingTrial_setScheduleRules',
    'onboardingTrial_enableTimeClock',
    'onboardingTrial_publishSchedule',
  ];

  onboardingTrialFlags.forEach((flag) => {
    if (!user.settings.clientFlags[flag]) {
      user.settings.clientFlags[flag] = { key: flag, completed: false };
    }
  });

  if (!('initialPositionsAndUsersSetup' in user.settings.clientFlags)) {
    user.settings.clientFlags.initialPositionsAndUsersSetup = true;
  }

  if (!('tagsFeatureEnabled' in user.settings.clientFlags)) {
    user.settings.clientFlags.tagsFeatureEnabled = false;
  }

  if (!('shiftScheduledOutOfPosition' in user.settings.clientFlags)) {
    user.settings.clientFlags.shiftScheduledOutOfPosition = {
      count: 0,
    };
  }

  if (!('shiftScheduledOutOfLocation' in user.settings.clientFlags)) {
    user.settings.clientFlags.shiftScheduledOutOfLocation = {
      count: 0,
    };
  }

  if (!('premiumDiscovery' in user.settings.clientFlags)) {
    user.settings.clientFlags.premiumDiscovery = {
      _relevant: false,
      _lastAutoShow: new Date().toISOString(),
    };
  }

  if (!('shiftTasksViewCount' in user.settings.clientFlags)) {
    user.settings.clientFlags.shiftTasksViewCount = 0;
  }

  if (!('settingsTips' in user.settings.clientFlags)) {
    user.settings.clientFlags.settingsTips = {
      companySettingsTipDismissed: false,
      integrationsTipDismissed: false,
      laborCostTipDismissed: false,
      notificationsTipDismissed: false,
      preferencesTipDismissed: false,
      scheduleTipDismissed: false,
      timeBlocksTipDismissed: false,
      timeclockTipDismissed: false,
      timeOffsTipDismissed: false,
    };
  }

  if (!('rosterColumns' in user.settings.clientFlags)) {
    user.settings.clientFlags.rosterColumns = [
      { active: true, property: 'employees' },
      { active: true, property: 'locations' },
      { active: true, property: 'positions' },
      { active: true, property: 'groups' },
      { active: true, property: 'tags', isPremium: true },
      { active: true, property: 'shiftTime' },
      { active: true, property: 'status', isPremium: true },
    ];
  }

  addPotentiallyMissingColumn(user, 'rosterColumns', 'employeeId', {
    after: 'employees',
    active: false,
  });
  addPotentiallyMissingColumn(user, 'timesheetsReportColumns', 'groups', {
    after: 'position',
    active: false,
  });
  addPotentiallyMissingColumn(user, 'noShowReportColumns', 'groups', {
    after: 'position',
    active: false,
  });
  removeObsoleteColumn(user, 'noShowReportColumns', 'scheduled');
  removeObsoleteColumn(user, 'noShowReportColumns', 'actual');
  addPotentiallyMissingColumn(user, 'noShowReportColumns', 'actualCount', {
    after: 'position',
    active: true,
  });
  addPotentiallyMissingColumn(user, 'noShowReportColumns', 'scheduledCount', {
    after: 'position',
    active: true,
  });

  addPotentiallyMissingColumn(user, 'timeoffReportColumns', 'fullLegalName', { active: true });
  addPotentiallyMissingColumn(user, 'noShowReportColumns', 'fullLegalName', { active: true });
  addPotentiallyMissingColumn(user, 'timesheetsReportColumns', 'fullLegalName', { active: true });
}

function addPotentiallyMissingColumn(
  user,
  columnsName,
  property,
  { active = true, after = null } = {}
) {
  if (!user.settings.clientFlags[columnsName].find((item) => item.property === property)) {
    if (after !== null) {
      const afterIndex = user.settings.clientFlags[columnsName].findIndex(
        (item) => item.property === after
      );
      if (afterIndex >= 0) {
        user.settings.clientFlags[columnsName].splice(afterIndex + 1, 0, { property, active });
        return;
      }
    }

    user.settings.clientFlags[columnsName].unshift({ property, active });
  }
}

function migrateColumn(user, columnsName, oldProperty, newProperty) {
  /**
   * If we ever need to rename a property (e.g. due to a typo) we need to call this to rename it
   * also for people who already have the old name in their clientFlags
   */
  const oldColumnName = user.settings.clientFlags[columnsName].find(
    (item) => item.property === oldProperty
  );
  if (oldColumnName) {
    oldColumnName.property = newProperty;
  }
}

// I see no point in jumping through hoops to reduce those simple ifs
// eslint-disable-next-line complexity
export function setUserDefaults(user, $localStorage) {
  if (!user.settings) {
    user.settings = {};
  }

  // This will break badly if server decides to set up some client flags by default
  // When a new user is created his clientFlags is an empty object.
  const isExistingUser =
    'settings' in user &&
    Boolean(user.settings.clientFlags) &&
    Object.keys(user.settings.clientFlags).length > 0;

  if (!user.orgSettings) {
    user.orgSettings = {};
  }

  if (typeof user.settings.showLastNameFirst === 'undefined') {
    user.settings.showLastNameFirst = false;
  }

  if (typeof user.orgSettings.firstDayOfWeek === 'undefined') {
    user.orgSettings.firstDayOfWeek = 1;
  }

  setDefaultClientFlags(user, isExistingUser);
  setOnboardingMigrationFlags(user, isExistingUser);
  setShiftAppearance(user.orgSettings);

  user.clientsettings = $localStorage.clientsettings || {};
  user.clientcache = $localStorage.clientcache || {};

  if (!isExistingUser) {
    user.clientcache.hiddenShiftsSidebar = false;
  }

  return isExistingUser;
}

function removeObsoleteColumn(user, columnsName, property) {
  const index = user.settings.clientFlags[columnsName].findIndex(
    (item) => item.property === property
  );
  if (index >= 0) {
    user.settings.clientFlags[columnsName].splice(index, 1);
  }
}

function setShiftAppearance(orgSettings) {
  if (!orgSettings.shiftAppearance) {
    orgSettings.shiftAppearance = {};
  }

  if (!orgSettings.shiftAppearance.day) {
    orgSettings.shiftAppearance.day = getAppearanceModeDefaults();
  }

  if (!orgSettings.shiftAppearance.week) {
    orgSettings.shiftAppearance.week = getAppearanceModeDefaults();
  }

  if (!orgSettings.shiftAppearance.month) {
    orgSettings.shiftAppearance.day = { ...getAppearanceModeDefaults(), fourthLine: [] };
  }
}

function getAppearanceModeDefaults() {
  return {
    firstLine: [],
    secondLine: [],
    thirdLine: [],
  };
}
